import { useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { ApiError, api } from '@/api/client';
import { FocusShell } from '@/components/layout';
import { Alert, Button, Card } from '@/components/ui';
import { useSession } from '@/hooks/session';
import { useLocale, useT } from '@/i18n';
import { detectCountry } from '@/lib/country';
import { getAttribution, trackCta } from '@/lib/analytics';

/** Same wording and version as sign-up: the consent a guest gives is the consent the account keeps. */
const CONSENT_VERSION = '2026-09-21';

/**
 * Start a check-in without an account. One confirmation (parent or guardian, and consent), then
 * straight to the child step. Sign-up is asked for when the check-in is done, before the result.
 */
export const GuestStart = () => {
  const t = useT();
  const { locale } = useLocale();
  const navigate = useNavigate();
  const { account, config, loading, signIn } = useSession();
  const [consent, setConsent] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!loading && account) return <Navigate to="/children" replace />;
  // Switched off (or an older API): the old first step.
  if (!loading && !config?.guestCheckin) return <Navigate to="/signup" replace />;

  const consentError = attempted && !consent ? t('signin.consentError') : undefined;

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setAttempted(true);
    if (!consent || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.auth.guest({
        consent: { version: CONSENT_VERSION, accepted: true },
        locale,
        detectedCountry: detectCountry(),
        attribution: getAttribution(),
      });
      trackCta('guest_started');
      signIn(res.token, res.account);
      navigate('/children', { replace: true });
    } catch (err) {
      if (!(err instanceof ApiError)) throw err;
      // Switched off between page load and click: sign-up still works.
      if (err.code === 'guest_unavailable') {
        navigate('/signup', { replace: true });
        return;
      }
      setError(err.status === 429 ? err.message : t('common.error'));
      setBusy(false);
    }
  };

  return (
    <FocusShell onBack={() => navigate('/')}>
      <Card>
        <h1 className="mb-1 text-h2">{t('guest.title')}</h1>
        <p className="text-body text-ink-muted">{t('guest.lede')}</p>
        <ul className="mt-4 grid gap-2 text-body text-ink">
          {(['guest.point1', 'guest.point2', 'guest.point3'] as const).map((key) => (
            <li key={key} className="flex gap-2">
              <span aria-hidden="true" className="font-bold text-teal-600">✓</span>
              <span>{t(key)}</span>
            </li>
          ))}
        </ul>
        <form onSubmit={onSubmit} noValidate className="mt-6 grid gap-5">
          {error && <Alert tone="warn">{error}</Alert>}
          <div>
            <label htmlFor="guest-consent" className="flex items-start gap-3 text-body text-ink">
              <input
                id="guest-consent"
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                aria-describedby={consentError ? 'guest-consent-error' : undefined}
                aria-invalid={consentError ? true : undefined}
                className="mt-1 h-5 w-5 shrink-0 rounded-sm border-sage-300 text-teal-600"
              />
              <span>
                {t('signin.consent')}{' '}
                <Link to="/privacy" className="text-lagoon-600 underline underline-offset-4">
                  {t('footer.privacy')}
                </Link>
              </span>
            </label>
            {consentError && (
              <p id="guest-consent-error" role="alert" className="mt-1 text-caption text-alert-ink">
                {consentError}
              </p>
            )}
          </div>
          <Button type="submit" size="lg" full loading={busy}>
            {t('guest.start')}
          </Button>
        </form>
        <p className="mt-5 text-body text-ink-muted">
          {t('guest.haveAccount')}{' '}
          <Link to="/signin" className="font-bold text-lagoon-600 underline-offset-4 hover:underline">
            {t('nav.signin')}
          </Link>
          {' · '}
          <Link to="/signup" className="font-bold text-lagoon-600 underline-offset-4 hover:underline">
            {t('guest.signupInstead')}
          </Link>
        </p>
      </Card>
    </FocusShell>
  );
};
